
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './ZolaidComputer.css';
import { useActionExecutor } from './ActionExecutor.js';
import AgentPointer from './AgentPointer.js';
import LivePreview from './LivePreview.js';
import DeveloperIDE from './DeveloperIDE.js';
import { createZipAndDownload, buildStandaloneHtmlFromParts, buildStaticZipFiles, buildIdeProjectZipEntries } from '../utils/exportZip.js';
import { webPreviewSlotForPath, looksLikeFullHtmlDocument, previewScrollHashForPath } from '../utils/webPreviewPath.js';
import { getIdeSiteStarterKit, previewTripleFromStarterKit } from '../utils/ideSiteStarterKit.js';
import { resolveVscodeEmbedUrl } from '../utils/vscodeEmbedUrl.js';

export default function ZolaidComputer({
  actions = [],
  isActive = false,
  onClose,
  currentTask = null,
  artifacts = null,
  onRequestFix = null,
  initialViewMode = 'code', // 'code' | 'preview' | 'vscode' (vscode only if embed URL is set)
  privacyEnabled = false,
  embedVSCodeUrl = null, // optional: code-server / self-hosted editor \u2014 third full-page workspace tab
  /** When true, fill parent (AgentSandbox) instead of floating overlay window. */
  embedded = false,
}) {
  const vsCodeEmbedUrl = resolveVscodeEmbedUrl(embedVSCodeUrl);
  const hasVscodeEmbed = Boolean(vsCodeEmbedUrl);
  const validViewModes = hasVscodeEmbed ? ['code', 'preview', 'vscode'] : ['code', 'preview'];
  const apiBase = () => {
    const apiUrl = (process.env.REACT_APP_API_URL || '').trim();
    return apiUrl ? apiUrl.replace(/\/+$/, '') : '';
  };
  const [localActions, setLocalActions] = useState([]); // actions queued from Code mode (patch apply)
  const [isMinimized, setIsMinimized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const VIEWMODE_LS_KEY = 'zolaid-computer-viewmode-v1';
  const normalizeViewMode = (v) => {
    const s = String(v || '').trim();
    if (s === 'builder' || s === 'logs') return 'code';
    return s;
  };
  const [viewMode, setViewMode] = useState(() => {
    const hasVs = Boolean(resolveVscodeEmbedUrl(embedVSCodeUrl));
    const allowed = hasVs ? ['code', 'preview', 'vscode'] : ['code', 'preview'];
    try {
      const fromProp = normalizeViewMode(initialViewMode);
      if (fromProp && allowed.includes(fromProp)) return fromProp;
      const raw = localStorage.getItem(VIEWMODE_LS_KEY);
      const saved = normalizeViewMode(raw);
      if (saved && allowed.includes(saved)) return saved;
    } catch (_) {}
    return hasVs ? 'vscode' : 'code';
  });

  const initialIdePreview = privacyEnabled
    ? { html: '', css: '', js: '' }
    : previewTripleFromStarterKit(getIdeSiteStarterKit());
  const [previewContent, setPreviewContent] = useState(() => ({ ...initialIdePreview }));
  const previewFullDoc = useMemo(() => {
    try {
      const raw = String(previewContent?.html || "").trim();
      if (!raw || !looksLikeFullHtmlDocument(raw)) return "";
      return String(previewContent.html || "");
    } catch (_) {
      return "";
    }
  }, [previewContent]);
  const [previewHistory, setPreviewHistory] = useState(() => [{ ...initialIdePreview }]); // index 0 = initial
  const [timelineIndex, setTimelineIndex] = useState(0);
  const [runtimeEvents, setRuntimeEvents] = useState([]);
  const [toast, setToast] = useState(null);
  const [previewEditModal, setPreviewEditModal] = useState(null); // { text, tagName } when open
  const [previewHostNav, setPreviewHostNav] = useState(null); // { type, hash, seq } \u2192 Live Preview iframe
  const idePreviewAnchorPathRef = useRef("");
  const [generatedFiles, setGeneratedFiles] = useState(() =>
    privacyEnabled ? {} : getIdeSiteStarterKit()
  ); // path -> content; built-in starter kit unless privacy mode
  const generatedFilesRef = useRef(generatedFiles);
  generatedFilesRef.current = generatedFiles;
  const previewSyncTimerRef = useRef(null);
  const pendingGeneratedPersistRef = useRef({}); // path -> content (batched)
  const persistTimerRef = useRef(null);
  const APPROVAL_LS_KEY = 'zolaid-computer-approval-v1';
  const [approvalMode, setApprovalMode] = useState(() => {
    try {
      const raw = localStorage.getItem(APPROVAL_LS_KEY);
      if (raw == null) return false;
      return String(raw) === '1';
    } catch (_) {
      return false;
    }
  });
  const [allowedTypes, setAllowedTypes] = useState(['moveCursor', 'click', 'type', 'createFile', 'updatePreview']);
  const CODE_CHROME_LS_KEY = 'zolaid-computer-codechrome-v1';
  const [chromeHidden, setChromeHidden] = useState(false);
  const [codeChromeOpen, setCodeChromeOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(CODE_CHROME_LS_KEY);
      if (raw == null) return false; // default: compact in Code mode
      return String(raw) === '1';
    } catch (_) {
      return false;
    }
  });
  const VIEWBAR_HIDDEN_LS_KEY = 'zolaid-computer-viewbar-hidden-v1';
  const [viewBarHidden, setViewBarHidden] = useState(() => {
    try {
      const raw = localStorage.getItem(VIEWBAR_HIDDEN_LS_KEY);
      if (raw == null) return false;
      return String(raw) === '1';
    } catch (_) {
      return false;
    }
  });
  const [viewDropdownOpen, setViewDropdownOpen] = useState(false);
  const viewDropdownRef = useRef(null);
  const resizingRef = useRef(null);
  const isCodeMode = viewMode === 'code';
  const isPreviewMode = viewMode === 'preview';
  const isVSCodeMode = viewMode === 'vscode' && hasVscodeEmbed;

  /** Browsers block https:// pages from embedding http:// iframes (empty frame). */
  const vscodeEmbedMixedContent = useMemo(() => {
    if (!vsCodeEmbedUrl || typeof window === 'undefined') return false;
    try {
      const u = new URL(vsCodeEmbedUrl, window.location.href);
      return window.location.protocol === 'https:' && u.protocol === 'http:';
    } catch (_) {
      return false;
    }
  }, [vsCodeEmbedUrl]);

  const DEFAULT_SIZE = { width: 800, height: 720 };
  const MIN_SIZE = { width: 520, height: 420 };
  const MAX_SIZE = { width: 1400, height: 980 };
  const VIEWPORT_MARGIN = 24;

  const clampToViewport = (size) => {
    const w = Number(size?.width);
    const h = Number(size?.height);
    const vw = Math.max(320, (window?.innerWidth || 0) - VIEWPORT_MARGIN);
    const vh = Math.max(320, (window?.innerHeight || 0) - VIEWPORT_MARGIN);
    return {
      width: Math.max(MIN_SIZE.width, Math.min(MAX_SIZE.width, Math.min(vw, Number.isFinite(w) ? w : DEFAULT_SIZE.width))),
      height: Math.max(MIN_SIZE.height, Math.min(MAX_SIZE.height, Math.min(vh, Number.isFinite(h) ? h : DEFAULT_SIZE.height))),
    };
  };

  const [panelSize, setPanelSize] = useState(() => {
    try {
      const raw = localStorage.getItem("zolaid-computer-size");
      if (!raw) return clampToViewport(DEFAULT_SIZE);
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return DEFAULT_SIZE;
      const w = Number(parsed.width);
      const h = Number(parsed.height);
      if (!Number.isFinite(w) || !Number.isFinite(h)) return DEFAULT_SIZE;
      return clampToViewport({ width: w, height: h });
    } catch (_) {
      return clampToViewport(DEFAULT_SIZE);
    }
  });

  useEffect(() => {
    if (privacyEnabled) return;
    try {
      localStorage.setItem("zolaid-computer-size", JSON.stringify(panelSize));
    } catch (_) {}
  }, [panelSize, privacyEnabled]);

  useEffect(() => {
    if (privacyEnabled) return;
    try {
      if (!viewMode) return;
      localStorage.setItem(VIEWMODE_LS_KEY, String(viewMode));
    } catch (_) {}
  }, [viewMode, privacyEnabled]);

  useEffect(() => {
    if (hasVscodeEmbed) return;
    if (viewMode !== 'vscode') return;
    setViewMode('code');
  }, [hasVscodeEmbed, viewMode]);

  useEffect(() => {
    if (privacyEnabled) return;
    try {
      localStorage.setItem(VIEWBAR_HIDDEN_LS_KEY, viewBarHidden ? '1' : '0');
    } catch (_) {}
  }, [viewBarHidden, privacyEnabled]);

  useEffect(() => {
    if (privacyEnabled) return;
    try {
      localStorage.setItem(APPROVAL_LS_KEY, approvalMode ? '1' : '0');
    } catch (_) {}
  }, [approvalMode, privacyEnabled]);

  // Close view dropdown when clicking outside
  useEffect(() => {
    if (!viewDropdownOpen) return;
    const onOutside = (e) => {
      if (viewDropdownRef.current && !viewDropdownRef.current.contains(e.target)) setViewDropdownOpen(false);
    };
    const t = setTimeout(() => window.addEventListener('click', onOutside), 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener('click', onOutside);
    };
  }, [viewDropdownOpen]);

  // Inactive minimal mode: hide workspace and show only "Zolaid is inactive" + quick icons.
  const INACTIVE_MINIMAL_LS_KEY = 'zolaid-computer-inactive-minimal-v1';
  const [inactiveMinimal, setInactiveMinimal] = useState(() => {
    try {
      const raw = localStorage.getItem(INACTIVE_MINIMAL_LS_KEY);
      if (raw == null) return true; // default: minimal when inactive
      return String(raw) === '1';
    } catch (_) {
      return true;
    }
  });

  useEffect(() => {
    if (privacyEnabled) return;
    try {
      localStorage.setItem(INACTIVE_MINIMAL_LS_KEY, inactiveMinimal ? '1' : '0');
    } catch (_) {}
  }, [inactiveMinimal, privacyEnabled]);

  // If Zolaid becomes active, automatically show the workspace again.
  useEffect(() => {
    try {
      if (isActive) setInactiveMinimal(false);
    } catch (_) {}
  }, [isActive]);

  useEffect(() => {
    if (privacyEnabled) return;
    try {
      localStorage.setItem(CODE_CHROME_LS_KEY, codeChromeOpen ? '1' : '0');
    } catch (_) {}
  }, [codeChromeOpen, privacyEnabled]);

  // Keyboard: 1 = Code, 2 = Preview, 3 = VS Code (when embed URL set); 3\u20134 = Preview if no embed
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target && (e.target.closest('input') || e.target.closest('textarea') || e.target.isContentEditable)) return;
      const key = e.key;
      const vs = Boolean(resolveVscodeEmbedUrl(embedVSCodeUrl));
      if (key === '1') {
        setViewMode('code');
        setViewDropdownOpen(false);
        e.preventDefault();
      } else if (key === '2') {
        setViewMode('preview');
        setViewDropdownOpen(false);
        e.preventDefault();
      } else if (key === '3') {
        setViewMode(vs ? 'vscode' : 'preview');
        setViewDropdownOpen(false);
        e.preventDefault();
      } else if (key === '4') {
        setViewMode('preview');
        setViewDropdownOpen(false);
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [embedVSCodeUrl]);

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = panelSize.width;
    const startH = panelSize.height;
    resizingRef.current = { startX, startY, startW, startH };

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setPanelSize(clampToViewport({ width: startW + dx, height: startH + dy }));
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      resizingRef.current = null;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const resetSize = () => {
    try {
      setPanelSize(clampToViewport(DEFAULT_SIZE));
      setToast('Size reset');
      window.clearTimeout(resetSize.__toastTimer);
      resetSize.__toastTimer = window.setTimeout(() => setToast(null), 1600);
    } catch (_) {}
  };

  const nudgeSize = (dw, dh) => {
    try {
      setPanelSize((prev) => clampToViewport({ width: (prev?.width || DEFAULT_SIZE.width) + dw, height: (prev?.height || DEFAULT_SIZE.height) + dh }));
    } catch (_) {}
  };

  const toggleFullscreen = () => {
    try {
      const el = document.querySelector('.zolaid-computer');
      const isFs = !!document.fullscreenElement;
      if (!isFs && el?.requestFullscreen) {
        // Browser fullscreen (hides tabs/address bar). Still cannot cover Windows taskbar in a normal browser.
        el.requestFullscreen().catch(() => {});
      } else if (isFs && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
      setIsExpanded((v) => !v);
    } catch (_) {
      setIsExpanded((v) => !v);
    }
  };

  const normalizeGeneratedPath = (p) => {
    return String(p || "")
      .replace(/\\/g, "/")
      .replace(/^\/+/, "")
      .trim();
  };

  /** After generated file map changes: full kit rebuild (starter) or single-slot sync (legacy triple). */
  const scheduleLivePreviewFromGenerated = (fallbackPath, fallbackContent) => {
    if (previewSyncTimerRef.current != null) {
      clearTimeout(previewSyncTimerRef.current);
      previewSyncTimerRef.current = null;
    }
    previewSyncTimerRef.current = setTimeout(() => {
      previewSyncTimerRef.current = null;
      const map = generatedFilesRef.current;
      if (map && typeof map === "object" && Object.prototype.hasOwnProperty.call(map, "frontend/index.html")) {
        try {
          setPreviewContent(previewTripleFromStarterKit(map));
          setRuntimeEvents([]);
        } catch (_) {}
      } else if (fallbackPath) {
        syncWebPreviewFromPath(fallbackPath, String(fallbackContent ?? ""));
      }
    }, 0);
  };

  const notifyPreviewScrollToFilePath = useCallback((filePath) => {
    try {
      const hash = previewScrollHashForPath(filePath);
      if (!hash) return;
      setPreviewHostNav((prev) => ({
        type: "scroll-to-hash",
        hash,
        seq: (prev && typeof prev.seq === "number" ? prev.seq : 0) + 1,
      }));
    } catch (_) {}
  }, []);

  const handleIdeGeneratedPathForPreview = useCallback(
    (path) => {
      const p = String(path || "").trim();
      if (!p) return;
      idePreviewAnchorPathRef.current = p;
      notifyPreviewScrollToFilePath(p);
    },
    [notifyPreviewScrollToFilePath]
  );

  useEffect(() => {
    if (viewMode !== "preview") return;
    const p = idePreviewAnchorPathRef.current;
    if (p) notifyPreviewScrollToFilePath(p);
  }, [viewMode, notifyPreviewScrollToFilePath]);

  /** Push editor/AI content into the Live Preview triple (html/css/js) for any web path. */
  const syncWebPreviewFromPath = (path, content) => {
    const p = normalizeGeneratedPath(path || "");
    const nextText = String(content ?? "");
    const slot = webPreviewSlotForPath(p);
    if (!slot) return;
    setPreviewContent((prev) => {
      const base = prev && typeof prev === "object" ? prev : { html: "", css: "", js: "" };
      const nextPreview = {
        html: slot === "html" ? nextText : String(base.html || ""),
        css: slot === "css" ? nextText : String(base.css || ""),
        js: slot === "js" ? nextText : String(base.js || ""),
      };
      try {
        setPreviewHistory((h) => {
          const arr = Array.isArray(h) ? h : [{ html: "", css: "", js: "" }];
          const copy = [...arr, nextPreview].slice(-200);
          setTimelineIndex(copy.length - 1);
          return copy;
        });
      } catch (_) {}
      return nextPreview;
    });
    setRuntimeEvents([]);
  };

  const enqueueActions = (next) => {
    const arr = Array.isArray(next) ? next : [];
    if (!arr.length) return;
    setLocalActions((prev) => [...prev, ...arr].slice(-500));
    setToast(`Queued ${arr.length} change${arr.length === 1 ? '' : 's'}`);
    window.clearTimeout(enqueueActions.__toastTimer);
    enqueueActions.__toastTimer = window.setTimeout(() => setToast(null), 1600);
  };

  const handleActionComplete = (action, index) => {
    let nextPreview = previewContent;
    if (action.type === 'updatePreview') {
      nextPreview = {
        html: action.html || '',
        css: action.css || '',
        js: action.js || ''
      };
      setPreviewContent(nextPreview);
      setRuntimeEvents([]); // reset runtime events per preview refresh

      // Keep a simple "exportable" snapshot in generated files too
      setGeneratedFiles((prev) => ({
        ...prev,
        'index.html': nextPreview.html || '',
        'styles.css': nextPreview.css || '',
        'app.js': nextPreview.js || '',
      }));
    }
    if (action.type === 'createFile' && action.content) {
      try {
        const p = normalizeGeneratedPath(action.path || '');
        if (p) {
          setGeneratedFiles((prev) => ({ ...prev, [p]: String(action.content || '') }));
        }
      } catch (_) {}
      // If it's HTML/CSS/JS file, update preview
      if (action.path.endsWith('.html') || action.path.endsWith('.htm')) {
        nextPreview = { ...nextPreview, html: action.content };
        setPreviewContent(nextPreview);
      } else if (action.path.endsWith('.css')) {
        nextPreview = { ...nextPreview, css: action.content };
        setPreviewContent(nextPreview);
      } else if (action.path.endsWith('.js')) {
        nextPreview = { ...nextPreview, js: action.content };
        setPreviewContent(nextPreview);
      }
    }

    // Append snapshot for timeline (index+1 because 0 is initial)
    setPreviewHistory(prev => {
      const copy = [...prev];
      copy[index + 1] = nextPreview;
      return copy;
    });
    setTimelineIndex(index + 1);
  };

  const updateGeneratedFile = (path, content) => {
    const p = normalizeGeneratedPath(path || '');
    if (!p) return;
    const nextText = String(content ?? '');
    setGeneratedFiles((prev) => {
      const base = prev && typeof prev === "object" ? prev : {};
      const next = { ...base, [p]: nextText };
      generatedFilesRef.current = next;
      return next;
    });
    scheduleLivePreviewFromGenerated(p, nextText);

    // Best-effort: persist to disk for terminal access (DEVTOOLS_ENABLED only)
    if (!privacyEnabled) {
      try {
        const base = apiBase();
        const endpoint = base ? `${base}/devtools/generated/write` : '/devtools/generated/write';
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ path: p, content: nextText }),
        }).catch(() => {});
      } catch (_) {}
    }
  };

  const deleteGeneratedFile = (path) => {
    const p = normalizeGeneratedPath(path || '');
    if (!p) return;
    setGeneratedFiles((prev) => {
      const base = prev && typeof prev === 'object' ? prev : {};
      const next = { ...base };
      delete next[p];
      generatedFilesRef.current = next;
      return next;
    });
    scheduleLivePreviewFromGenerated(null, null);
    if (!privacyEnabled) {
      try {
        const base = apiBase();
        const endpoint = base ? `${base}/devtools/generated/delete` : '/devtools/generated/delete';
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ path: p }),
        }).catch(() => {});
      } catch (_) {}
    }
  };

  const renameGeneratedFile = (fromPath, toPath) => {
    const from = normalizeGeneratedPath(fromPath || '');
    const to = normalizeGeneratedPath(toPath || '');
    if (!from || !to) return;
    setGeneratedFiles((prev) => {
      const base = prev && typeof prev === 'object' ? prev : {};
      const next = { ...base };
      const val = Object.prototype.hasOwnProperty.call(next, from) ? next[from] : undefined;
      if (val !== undefined) {
        next[to] = String(val);
        delete next[from];
      }
      generatedFilesRef.current = next;
      return next;
    });
    scheduleLivePreviewFromGenerated(null, null);
    if (!privacyEnabled) {
      try {
        const base = apiBase();
        const endpoint = base ? `${base}/devtools/generated/rename` : '/devtools/generated/rename';
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ from, to }),
        }).catch(() => {});
      } catch (_) {}
    }
  };

  const queuePersistGeneratedFiles = (fileMap) => {
    if (privacyEnabled) return;
    try {
      const src = fileMap && typeof fileMap === "object" ? fileMap : null;
      if (!src) return;
      const pending = pendingGeneratedPersistRef.current && typeof pendingGeneratedPersistRef.current === "object"
        ? pendingGeneratedPersistRef.current
        : {};
      for (const [rawPath, rawContent] of Object.entries(src)) {
        const p = normalizeGeneratedPath(rawPath || "");
        if (!p) continue;
        pending[p] = String(rawContent ?? "");
      }
      pendingGeneratedPersistRef.current = pending;
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(() => {
        try {
          const toWrite = pendingGeneratedPersistRef.current && typeof pendingGeneratedPersistRef.current === "object"
            ? pendingGeneratedPersistRef.current
            : {};
          pendingGeneratedPersistRef.current = {};
          const entries = Object.entries(toWrite);
          if (!entries.length) return;
          const base = apiBase();
          const endpoint = base ? `${base}/devtools/generated/write` : "/devtools/generated/write";
          // Best-effort: write in background; ignore failures (DEVTOOLS may be disabled).
          Promise.allSettled(
            entries.slice(0, 600).map(([p, content]) =>
              fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ path: p, content: String(content ?? "") }),
              })
            )
          ).catch(() => {});
        } catch (_) {}
      }, 50);
    } catch (_) {}
  };

  // Convert legacy actions to structured actions if needed
  const mapToStructured = (action) => {
    if (action.type && ['moveCursor', 'click', 'type', 'createFile', 'updatePreview'].includes(action.type)) {
      return action;
    }
    if (action.code) {
      return {
        type: 'updatePreview',
        html: action.code,
        css: '',
        js: '',
        delay: 0,
        description: 'Update preview with generated code'
      };
    }
    return {
      type: 'execute',
      delay: 0,
      description: action.description || `${action.type} action`
    };
  };

  const baseStructuredActions = useMemo(() => actions.map(mapToStructured), [actions]);
  const localStructuredActions = useMemo(() => localActions.map(mapToStructured), [localActions]);
  const structuredActions = useMemo(() => [...baseStructuredActions, ...localStructuredActions], [baseStructuredActions, localStructuredActions]);

  const pushPreviewSnapshot = (snap) => {
    try {
      const nextPreview = {
        html: snap?.html || '',
        css: snap?.css || '',
        js: snap?.js || '',
      };
      setPreviewContent(nextPreview);
      setRuntimeEvents([]);
      // Also mirror preview + any provided file map into Generated files (Code view).
      try {
        const incoming = snap?.files && typeof snap.files === "object" ? snap.files : null;
        const persistMap = {};
        if (incoming) {
          for (const [rawPath, content] of Object.entries(incoming)) {
            const p = normalizeGeneratedPath(rawPath || "");
            if (!p) continue;
            persistMap[p] = String(content ?? "");
          }
        }
        // Always keep a simple "exportable" snapshot too
        if (nextPreview.html) persistMap["index.html"] = String(nextPreview.html);
        if (nextPreview.css) persistMap["styles.css"] = String(nextPreview.css);
        if (nextPreview.js) persistMap["app.js"] = String(nextPreview.js);

        setGeneratedFiles((prev) => {
          const base = prev && typeof prev === "object" ? prev : {};
          return { ...base, ...persistMap };
        });

        // Persist to disk so Code mode + terminal can see them (best-effort).
        queuePersistGeneratedFiles(persistMap);
      } catch (_) {}
      setPreviewHistory((prev) => {
        const base = Array.isArray(prev) ? prev : [{ html: '', css: '', js: '' }];
        const copy = [...base, nextPreview].slice(-200);
        // move to latest snapshot
        setTimelineIndex(copy.length - 1);
        return copy;
      });
      // When builder generates (payment, login Apple/Facebook/Microsoft, etc.), switch to Code so code appears in IDE immediately.
      const src = String(snap?.source || "");
      const focusPreview = snap?.focusPreview === true;
      if (src === "builder_generate") {
        setViewMode("code");
        setToast("Generated \u2014 opened in Code");
      } else {
        const shouldSwitch = focusPreview || !isCodeMode;
        if (shouldSwitch) setViewMode(focusPreview ? "preview" : "code");
        setToast(isCodeMode ? "Generated files updated" : "Sent to preview");
      }
      window.clearTimeout(pushPreviewSnapshot.__toastTimer);
      pushPreviewSnapshot.__toastTimer = window.setTimeout(() => setToast(null), 1600);
    } catch (_) {}
  };

  const handleRuntimeEvent = (evt) => {
    if (!evt || typeof evt !== 'object') return;
    setRuntimeEvents(prev => [...prev, evt].slice(-500));
    if (evt.type === 'alert') {
      setToast(evt.message || 'Alert');
      window.clearTimeout(handleRuntimeEvent.__toastTimer);
      handleRuntimeEvent.__toastTimer = window.setTimeout(() => setToast(null), 3000);
    }
    if (evt.type === 'preview-edit-request' && evt.text != null) {
      setPreviewEditModal({ text: String(evt.text), tagName: evt.tagName || '', value: String(evt.text) });
    }
  };

  const buildStandaloneHtml = (snap) => {
    return buildStandaloneHtmlFromParts({
      html: snap?.html || '',
      css: snap?.css || '',
      js: snap?.js || '',
      title: 'Zolaid Export',
    });
  };

  const [deployOpen, setDeployOpen] = useState(false);
  const [deployProvider, setDeployProvider] = useState('github-pages'); // github-pages | vercel | netlify | aws-s3 | aws-cloudfront | gcp-gcs | cloudflare-pages | firebase-hosting | azure-storage
  const [deployRepoName, setDeployRepoName] = useState('zolaid-site');
  const [deployToken, setDeployToken] = useState('');
  const [deployStatus, setDeployStatus] = useState('');
  const [deployUrl, setDeployUrl] = useState('');
  const [awsRegion, setAwsRegion] = useState('us-east-1');
  const [awsBucket, setAwsBucket] = useState('');
  const [awsAccessKeyId, setAwsAccessKeyId] = useState('');
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState('');
  const [awsSessionToken, setAwsSessionToken] = useState('');
  const [awsMakePublic, setAwsMakePublic] = useState(true);
  const [gcpBucket, setGcpBucket] = useState('');
  const [gcpServiceAccountJson, setGcpServiceAccountJson] = useState('');
  const [cfAccountId, setCfAccountId] = useState('');
  const [cfProjectName, setCfProjectName] = useState('zolaid-site');
  const [cfToken, setCfToken] = useState('');
  const [fbProjectId, setFbProjectId] = useState('');
  const [fbSiteId, setFbSiteId] = useState('');
  const [fbToken, setFbToken] = useState('');
  const [azureConnectionString, setAzureConnectionString] = useState('');
  const [azureAccountName, setAzureAccountName] = useState('');
  const [azureAccountKey, setAzureAccountKey] = useState('');

  const deployToGitHubPages = async () => {
    try {
      const token = String(deployToken || '').trim();
      if (!token) {
        setDeployStatus('Missing GitHub token');
        return;
      }
      const repoName = String(deployRepoName || '').trim() || 'zolaid-site';
      setDeployStatus('Deploying\u2026');
      setDeployUrl('');

      const apiUrl = (process.env.REACT_APP_API_URL || '').trim();
      const endpoint = apiUrl ? `${apiUrl}/deploy/github-pages` : '/deploy/github-pages';

      const htmlDoc = buildStandaloneHtml(previewContent);
      const files = {
        'index.html': htmlDoc,
        'styles.css': previewContent.css || '',
        'app.js': previewContent.js || '',
      };

      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, repoName, visibility: 'public', files }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setDeployStatus(data?.error || `Deploy failed (${resp.status})`);
        return;
      }
      setDeployStatus(data?.note || 'Deployed');
      setDeployUrl(String(data?.url || ''));
    } catch (e) {
      setDeployStatus(e?.message || 'Deploy failed');
    }
  };

  const deployToVercel = async () => {
    try {
      const token = String(deployToken || '').trim();
      if (!token) {
        setDeployStatus('Missing Vercel token');
        return;
      }
      const projectName = String(deployRepoName || '').trim() || 'zolaid-site';
      setDeployStatus('Deploying\u2026');
      setDeployUrl('');

      const apiUrl = (process.env.REACT_APP_API_URL || '').trim();
      const endpoint = apiUrl ? `${apiUrl}/deploy/vercel` : '/deploy/vercel';

      const htmlDoc = buildStandaloneHtml(previewContent);
      const files = {
        'index.html': htmlDoc,
        'styles.css': previewContent.css || '',
        'app.js': previewContent.js || '',
      };

      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, projectName, files }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setDeployStatus(data?.error || `Deploy failed (${resp.status})`);
        return;
      }
      setDeployStatus(data?.note || 'Deployed');
      setDeployUrl(String(data?.url || ''));
    } catch (e) {
      setDeployStatus(e?.message || 'Deploy failed');
    }
  };

  const deployToNetlify = async () => {
    try {
      const token = String(deployToken || '').trim();
      if (!token) {
        setDeployStatus('Missing Netlify token');
        return;
      }
      const siteName = String(deployRepoName || '').trim() || 'zolaid-site';
      setDeployStatus('Deploying\u2026');
      setDeployUrl('');

      const apiUrl = (process.env.REACT_APP_API_URL || '').trim();
      const endpoint = apiUrl ? `${apiUrl}/deploy/netlify` : '/deploy/netlify';

      const htmlDoc = buildStandaloneHtml(previewContent);
      const files = {
        'index.html': htmlDoc,
        'styles.css': previewContent.css || '',
        'app.js': previewContent.js || '',
      };

      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, siteName, files }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setDeployStatus(data?.error || `Deploy failed (${resp.status})`);
        return;
      }
      setDeployStatus(data?.note || 'Deployed');
      setDeployUrl(String(data?.url || ''));
    } catch (e) {
      setDeployStatus(e?.message || 'Deploy failed');
    }
  };

  const deployToAwsS3 = async () => {
    try {
      const region = String(awsRegion || '').trim() || 'us-east-1';
      const bucket = String(awsBucket || '').trim();
      const accessKeyId = String(awsAccessKeyId || '').trim();
      const secretAccessKey = String(awsSecretAccessKey || '').trim();
      const sessionToken = String(awsSessionToken || '').trim();
      const makePublic = !!awsMakePublic;

      if (!bucket) {
        setDeployStatus('Missing S3 bucket');
        return;
      }
      if (!accessKeyId) {
        setDeployStatus('Missing AWS access key');
        return;
      }
      if (!secretAccessKey) {
        setDeployStatus('Missing AWS secret key');
        return;
      }

      setDeployStatus('Deploying\u2026');
      setDeployUrl('');

      const apiUrl = (process.env.REACT_APP_API_URL || '').trim();
      const endpoint = apiUrl ? `${apiUrl}/deploy/aws-s3` : '/deploy/aws-s3';

      const htmlDoc = buildStandaloneHtml(previewContent);
      const files = {
        'index.html': htmlDoc,
        'styles.css': previewContent.css || '',
        'app.js': previewContent.js || '',
      };

      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ region, bucket, accessKeyId, secretAccessKey, sessionToken: sessionToken || undefined, makePublic, files }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setDeployStatus(data?.error || `Deploy failed (${resp.status})`);
        return;
      }
      setDeployStatus(data?.note || 'Deployed');
      setDeployUrl(String(data?.url || ''));
    } catch (e) {
      setDeployStatus(e?.message || 'Deploy failed');
    }
  };

  const deployToAwsCloudFront = async () => {
    try {
      const region = String(awsRegion || '').trim() || 'us-east-1';
      const bucket = String(awsBucket || '').trim();
      const accessKeyId = String(awsAccessKeyId || '').trim();
      const secretAccessKey = String(awsSecretAccessKey || '').trim();
      const sessionToken = String(awsSessionToken || '').trim();

      if (!bucket) {
        setDeployStatus('Missing S3 bucket');
        return;
      }
      if (!accessKeyId) {
        setDeployStatus('Missing AWS access key');
        return;
      }
      if (!secretAccessKey) {
        setDeployStatus('Missing AWS secret key');
        return;
      }

      setDeployStatus('Deploying\u2026');
      setDeployUrl('');

      const apiUrl = (process.env.REACT_APP_API_URL || '').trim();
      const endpoint = apiUrl ? `${apiUrl}/deploy/aws-cloudfront` : '/deploy/aws-cloudfront';

      const htmlDoc = buildStandaloneHtml(previewContent);
      const files = {
        'index.html': htmlDoc,
        'styles.css': previewContent.css || '',
        'app.js': previewContent.js || '',
      };

      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ region, bucket, accessKeyId, secretAccessKey, sessionToken: sessionToken || undefined, files }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setDeployStatus(data?.error || `Deploy failed (${resp.status})`);
        return;
      }
      setDeployStatus(data?.note || 'Deployed');
      setDeployUrl(String(data?.url || data?.cloudFront?.domain || ''));
    } catch (e) {
      setDeployStatus(e?.message || 'Deploy failed');
    }
  };

  const deployToGcpGcs = async () => {
    try {
      const bucket = String(gcpBucket || '').trim();
      const serviceAccountJson = String(gcpServiceAccountJson || '').trim();
      if (!bucket) {
        setDeployStatus('Missing GCS bucket');
        return;
      }
      if (!serviceAccountJson) {
        setDeployStatus('Missing service account JSON');
        return;
      }

      setDeployStatus('Deploying\u2026');
      setDeployUrl('');

      const apiUrl = (process.env.REACT_APP_API_URL || '').trim();
      const endpoint = apiUrl ? `${apiUrl}/deploy/gcp-gcs` : '/deploy/gcp-gcs';

      const htmlDoc = buildStandaloneHtml(previewContent);
      const files = {
        'index.html': htmlDoc,
        'styles.css': previewContent.css || '',
        'app.js': previewContent.js || '',
      };

      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bucket, serviceAccountJson, files }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setDeployStatus(data?.error || `Deploy failed (${resp.status})`);
        return;
      }
      setDeployStatus(data?.note || 'Deployed');
      setDeployUrl(String(data?.url || ''));
    } catch (e) {
      setDeployStatus(e?.message || 'Deploy failed');
    }
  };

  const deployToCloudflarePages = async () => {
    try {
      const accountId = String(cfAccountId || '').trim();
      const projectName = String(cfProjectName || '').trim();
      const token = String(cfToken || '').trim();
      if (!accountId) {
        setDeployStatus('Missing Cloudflare account id');
        return;
      }
      if (!projectName) {
        setDeployStatus('Missing Cloudflare project name');
        return;
      }
      if (!token) {
        setDeployStatus('Missing Cloudflare token');
        return;
      }

      setDeployStatus('Deploying\u2026');
      setDeployUrl('');

      const apiUrl = (process.env.REACT_APP_API_URL || '').trim();
      const endpoint = apiUrl ? `${apiUrl}/deploy/cloudflare-pages` : '/deploy/cloudflare-pages';

      const htmlDoc = buildStandaloneHtml(previewContent);
      const files = {
        'index.html': htmlDoc,
        'styles.css': previewContent.css || '',
        'app.js': previewContent.js || '',
      };

      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId, projectName, token, files }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setDeployStatus(data?.error || `Deploy failed (${resp.status})`);
        return;
      }
      setDeployStatus(data?.note || 'Deployed');
      setDeployUrl(String(data?.url || ''));
    } catch (e) {
      setDeployStatus(e?.message || 'Deploy failed');
    }
  };

  const deployToFirebaseHosting = async () => {
    try {
      const projectId = String(fbProjectId || '').trim();
      const siteId = String(fbSiteId || '').trim();
      const token = String(fbToken || '').trim();
      if (!projectId) {
        setDeployStatus('Missing Firebase project id');
        return;
      }
      if (!token) {
        setDeployStatus('Missing Firebase token');
        return;
      }

      setDeployStatus('Deploying\u2026');
      setDeployUrl('');

      const apiUrl = (process.env.REACT_APP_API_URL || '').trim();
      const endpoint = apiUrl ? `${apiUrl}/deploy/firebase-hosting` : '/deploy/firebase-hosting';

      const htmlDoc = buildStandaloneHtml(previewContent);
      const files = {
        'index.html': htmlDoc,
        'styles.css': previewContent.css || '',
        'app.js': previewContent.js || '',
      };

      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId, siteId: siteId || undefined, token, files }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setDeployStatus(data?.error || `Deploy failed (${resp.status})`);
        return;
      }
      setDeployStatus(data?.note || 'Deployed');
      setDeployUrl(String(data?.url || ''));
    } catch (e) {
      setDeployStatus(e?.message || 'Deploy failed');
    }
  };

  const deployToAzureStorage = async () => {
    try {
      const connectionString = String(azureConnectionString || '').trim();
      const accountName = String(azureAccountName || '').trim();
      const accountKey = String(azureAccountKey || '').trim();

      if (!connectionString && !(accountName && accountKey)) {
        setDeployStatus('Provide Azure connection string or account name + key');
        return;
      }

      setDeployStatus('Deploying\u2026');
      setDeployUrl('');

      const apiUrl = (process.env.REACT_APP_API_URL || '').trim();
      const endpoint = apiUrl ? `${apiUrl}/deploy/azure-storage` : '/deploy/azure-storage';

      const htmlDoc = buildStandaloneHtml(previewContent);
      const files = {
        'index.html': htmlDoc,
        'styles.css': previewContent.css || '',
        'app.js': previewContent.js || '',
      };

      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          connectionString: connectionString || undefined,
          accountName: accountName || undefined,
          accountKey: accountKey || undefined,
          files,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setDeployStatus(data?.error || `Deploy failed (${resp.status})`);
        return;
      }
      setDeployStatus(data?.note || 'Deployed');
      setDeployUrl(String(data?.url || ''));
    } catch (e) {
      setDeployStatus(e?.message || 'Deploy failed');
    }
  };

  const copyText = async (text, okMsg = 'Copied') => {
    try {
      await navigator.clipboard.writeText(text || '');
      setToast(okMsg);
      window.clearTimeout(copyText.__toastTimer);
      copyText.__toastTimer = window.setTimeout(() => setToast(null), 1600);
    } catch (_) {
      setToast('Copy failed');
      window.clearTimeout(copyText.__toastTimer);
      copyText.__toastTimer = window.setTimeout(() => setToast(null), 2000);
    }
  };

  const downloadTextFile = (filename, content, mime = 'text/plain;charset=utf-8') => {
    try {
      const blob = new Blob([content || ''], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) {
      setToast('Download failed');
      window.clearTimeout(downloadTextFile.__toastTimer);
      downloadTextFile.__toastTimer = window.setTimeout(() => setToast(null), 2000);
    }
  };

  /** Repo + `.zolaid_generated` mirror from DevTools (requires backend + DEVTOOLS_ENABLED). */
  const fetchIdeExportFromDevTools = async () => {
    const base = apiBase();
    const endpoint = base ? `${base}/devtools/export/ide-files` : '/devtools/export/ide-files';
    const resp = await fetch(endpoint, { credentials: 'include' });
    if (!resp.ok) throw new Error(`export ${resp.status}`);
    const data = await resp.json();
    return data.files && typeof data.files === 'object' ? data.files : {};
  };

  const verification = useMemo(() => {
    const recent = runtimeEvents.slice(-200);
    const hardErrors = recent.filter(e =>
      e?.type === 'error' ||
      e?.type === 'unhandledrejection' ||
      (e?.type === 'console' && e?.level === 'error')
    );
    return {
      ok: hardErrors.length === 0,
      errorCount: hardErrors.length,
      lastError: hardErrors[hardErrors.length - 1] || null
    };
  }, [runtimeEvents]);

  const canRequestFix = Boolean(onRequestFix) && !verification.ok;

  const {
    currentAction,
    executedActions,
    isExecuting,
    awaitingApproval,
    approveNext,
    skipNext,
    resetTo,
    currentActionIndex,
    progress
  } = useActionExecutor(
    // If the computer is "inactive", still allow applying queued Code-mode actions.
    structuredActions,
    handleActionComplete,
    {
      startIndex: 0,
      requireApproval: approvalMode,
      allowedActionTypes: allowedTypes
    }
  );

  // When user scrubs timeline, show the preview snapshot at that point
  useEffect(() => {
    const snap = previewHistory[timelineIndex];
    if (snap) setPreviewContent(snap);
  }, [timelineIndex, previewHistory]);

  const latestAction = structuredActions.length > 0 ? structuredActions[structuredActions.length - 1] : null;
  const hasActiveTask = isActive || currentTask || isExecuting;

  useEffect(() => {
    const next = normalizeViewMode(initialViewMode);
    if (!next) return;
    if (!validViewModes.includes(next)) return;
    setViewMode(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialViewMode, hasVscodeEmbed]);

  return (
    <div
      className={`zolaid-computer ${isMinimized && !embedded ? 'minimized' : ''} ${isExpanded && !embedded ? 'expanded' : ''} ${embedded ? 'embedded' : ''}`}
      style={
        embedded
          ? {
              width: '100%',
              height: '100%',
              minWidth: 0,
              minHeight: 0,
              maxWidth: 'none',
              maxHeight: 'none',
              top: 0,
              left: 0,
              transform: 'none',
            }
          : isMinimized
          ? undefined
          : isExpanded
            ? {
                width: '100vw',
                height: '100vh',
                minWidth: `${MIN_SIZE.width}px`,
                minHeight: `${MIN_SIZE.height}px`,
                maxWidth: '100vw',
                maxHeight: '100vh',
                top: 0,
                left: 0,
                transform: 'none',
              }
          : {
              width: `${panelSize.width}px`,
              height: `${panelSize.height}px`,
              minWidth: `${MIN_SIZE.width}px`,
              minHeight: `${MIN_SIZE.height}px`,
              maxWidth: `${MAX_SIZE.width}px`,
              maxHeight: `${MAX_SIZE.height}px`,
            }
      }
    >
      {/* Agent pointer overlay */}
      {isExecuting && currentAction && (
        <AgentPointer
          action={currentAction}
          isVisible={['moveCursor', 'click', 'type'].includes(currentAction.type)}
          onComplete={() => {}}
        />
      )}

      {/* Resize Handle (floating window only) */}
      {!embedded && <div className="zolaid-resize-handle"></div>}
      {!embedded && <div className="zolaid-resize-corner" onMouseDown={startResize} title="Resize"></div>}

      {/* Main Container */}
      <div className="zolaid-computer-wrapper">
        {toast && (
          <div className="zolaid-toast" role="status" aria-live="polite">
            {toast}
          </div>
        )}
        {previewEditModal && (
          <div
            className="zolaid-preview-edit-overlay"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
            }}
            onClick={() => setPreviewEditModal(null)}
          >
            <div
              className="zolaid-preview-edit-modal"
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: 20,
                minWidth: 320,
                maxWidth: '90vw',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ fontWeight: 700, marginBottom: 10 }}>
                {/^h[1-6]$/.test(String(previewEditModal.tagName || '')) ? 'Edit header' : 'Edit text'}
              </div>
              {/^h[1-6]$/.test(String(previewEditModal.tagName || '')) ? (
                <input
                  type="text"
                  autoFocus
                  value={previewEditModal.value ?? previewEditModal.text}
                  onChange={(e) => setPreviewEditModal((m) => ({ ...m, value: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setPreviewEditModal(null);
                  }}
                  placeholder="Type your header\u2026"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: 16,
                    border: '1px solid #ccc',
                    borderRadius: 8,
                    boxSizing: 'border-box',
                  }}
                />
              ) : (
                <textarea
                  autoFocus
                  value={previewEditModal.value ?? previewEditModal.text}
                  onChange={(e) => setPreviewEditModal((m) => ({ ...m, value: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setPreviewEditModal(null);
                  }}
                  style={{
                    width: '100%',
                    minHeight: 60,
                    padding: 10,
                    fontSize: 14,
                    border: '1px solid #ccc',
                    borderRadius: 8,
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                  rows={3}
                />
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button
                  type="button"
                  className="zolaid-btn"
                  onClick={() => setPreviewEditModal(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="zolaid-btn"
                  onClick={() => {
                    const oldText = previewEditModal.text;
                    const newText = String(previewEditModal.value ?? previewEditModal.text).trim();
                    setPreviewEditModal(null);
                    if (oldText === newText) return;
                    const html = previewContent.html || '';
                    const idx = html.indexOf(oldText);
                    if (idx === -1) return;
                    const newHtml = html.slice(0, idx) + newText + html.slice(idx + oldText.length);
                    setPreviewContent((prev) => ({ ...prev, html: newHtml }));
                    setToast('Preview updated');
                    if (typeof window !== 'undefined' && window.clearTimeout) {
                      window.clearTimeout(handleRuntimeEvent.__toastTimer);
                      handleRuntimeEvent.__toastTimer = window.setTimeout(() => setToast(null), 2000);
                    }
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="zolaid-computer-header">
          <div className="zolaid-header-left">
            <div className="zolaid-computer-title">
              <span className="zolaid-icon">{'\uD83D\uDDA5\uFE0F'}</span>
            </div>
            {!embedded && (
            <button 
              className="zolaid-btn dropdown-btn"
              onClick={toggleFullscreen}
              title={isExpanded ? "Exit fullscreen" : "Fullscreen"}
            >
              <svg width="20" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M3.38477 3.33341C2.92453 3.33341 2.55143 3.70651 2.55143 4.16675V12.5001C2.55143 12.9603 2.92453 13.3334 3.38477 13.3334H16.7181C17.1783 13.3334 17.5514 12.9603 17.5514 12.5001V4.16675C17.5514 3.70651 17.1783 3.33341 16.7181 3.33341H3.38477ZM0.884766 4.16675C0.884766 2.78604 2.00405 1.66675 3.38477 1.66675H16.7181C18.0988 1.66675 19.2181 2.78604 19.2181 4.16675V12.5001C19.2181 13.8808 18.0988 15.0001 16.7181 15.0001H3.38477C2.00405 15.0001 0.884766 13.8808 0.884766 12.5001V4.16675Z" fill="currentColor"></path>
                <path fillRule="evenodd" clipRule="evenodd" d="M5.88477 17.5001C5.88477 17.0398 6.25786 16.6667 6.7181 16.6667H13.3848C13.845 16.6667 14.2181 17.0398 14.2181 17.5001C14.2181 17.9603 13.845 18.3334 13.3848 18.3334H6.7181C6.25786 18.3334 5.88477 17.9603 5.88477 17.5001Z" fill="currentColor"></path>
                <path fillRule="evenodd" clipRule="evenodd" d="M10.0501 13.3333C10.5104 13.3333 10.8835 13.7063 10.8835 14.1666V17.4999C10.8835 17.9602 10.5104 18.3333 10.0501 18.3333C9.58989 18.3333 9.2168 17.9602 9.2168 17.4999V14.1666C9.2168 13.7063 9.58989 13.3333 10.0501 13.3333Z" fill="currentColor"></path>
                <path d="M8.11331 6.25002C8.10195 6.22379 8.09873 6.19475 8.10408 6.16666C8.10943 6.13858 8.1231 6.11275 8.14332 6.09254C8.16353 6.07232 8.18936 6.05865 8.21744 6.0533C8.24553 6.04795 8.27457 6.05117 8.3008 6.06253L12.9088 7.93453C12.9368 7.94595 12.9605 7.9659 12.9766 7.99155C12.9926 8.01721 13.0002 8.04726 12.9982 8.07745C12.9961 8.10764 12.9847 8.13643 12.9654 8.15973C12.9461 8.18303 12.9199 8.19967 12.8907 8.20727L11.1269 8.66231C11.0273 8.68793 10.9363 8.73977 10.8635 8.81245C10.7907 8.88513 10.7386 8.97599 10.7128 9.07559L10.2581 10.8399C10.2504 10.8692 10.2338 10.8953 10.2105 10.9146C10.1872 10.9339 10.1584 10.9454 10.1282 10.9474C10.098 10.9494 10.068 10.9418 10.0423 10.9258C10.0167 10.9098 9.99673 10.886 9.98531 10.858L8.11331 6.25002Z" fill="currentColor" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"></path>
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m6 9 6 6 6-6"></path>
              </svg>
            </button>
            )}
          </div>
          <div className="zolaid-header-right">
            <div className="zolaid-status-indicator">
              <div className={`status-dot ${hasActiveTask ? 'active' : 'inactive'}`}></div>
              <span className="status-text">{hasActiveTask ? 'Zolaid is active' : 'Zolaid is inactive'}</span>
            </div>
            {!embedded && (
              <>
                <div className="zolaid-header-divider"></div>
                <button
                  className="zolaid-btn"
                  onClick={resetSize}
                  title="Reset size"
                  type="button"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-2.64-6.36"></path>
                    <path d="M21 3v6h-6"></path>
                  </svg>
                </button>
                <button className="zolaid-btn" onClick={() => nudgeSize(-80, -80)} title="Smaller" type="button">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14"></path>
                  </svg>
                </button>
                <button className="zolaid-btn" onClick={toggleFullscreen} title={isExpanded ? "Exit fullscreen" : "Fullscreen"} type="button">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5v14"></path>
                    <path d="M5 12h14"></path>
                  </svg>
                </button>
              </>
            )}
            {isCodeMode && (
              <button
                className="zolaid-btn"
                type="button"
                onClick={() => {
                  setCodeChromeOpen((v) => !v);
                }}
                title={codeChromeOpen ? "Hide toolbar (more room)" : "Show toolbar"}
              >
                <span style={{ fontSize: 14, lineHeight: "14px" }} aria-hidden="true">
                  {'\uD83E\uDDF0'}
                </span>
              </button>
            )}
            {!embedded && (
              <>
                <div className="zolaid-header-divider"></div>
                <button 
                  className="zolaid-btn minimize-btn"
                  onClick={() => setIsMinimized(!isMinimized)}
                  title={isMinimized ? "Expand" : "Minimize"}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 18 14" fill="none">
                    <path d="M3.75 3.85C3.75 3.51863 4.01863 3.25 4.35 3.25L13.65 3.25C13.9814 3.25 14.25 3.51863 14.25 3.85L14.25 10.15C14.25 10.4814 13.9814 10.75 13.65 10.75L4.35 10.75C4.01863 10.75 3.75 10.4814 3.75 10.15L3.75 3.85Z" fill="currentColor"></path>
                    <path d="M15.75 2.5C15.75 2.08579 15.4142 1.75 15 1.75L3 1.75C2.58579 1.75 2.25 2.08579 2.25 2.5L2.25 11.5C2.25 11.9142 2.58579 12.25 3 12.25L15 12.25C15.4142 12.25 15.75 11.9142 15.75 11.5L15.75 2.5ZM17.25 11.5C17.25 12.7426 16.2426 13.75 15 13.75L3 13.75C1.75736 13.75 0.75 12.7426 0.75 11.5L0.75 2.5C0.75 1.25736 1.75736 0.25 3 0.25L15 0.25C16.2426 0.25 17.25 1.25736 17.25 2.5L17.25 11.5Z" fill="currentColor"></path>
                  </svg>
                </button>
                <div className="zolaid-header-divider"></div>
                {onClose && (
                  <button 
                    className="zolaid-btn close-btn"
                    onClick={onClose}
                    title="Close"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18"></path>
                      <path d="m6 6 12 12"></path>
                    </svg>
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {!isMinimized && (
          <div className="zolaid-computer-content">
            {chromeHidden && (
              <div
                style={{
                  flexShrink: 0,
                  padding: '6px 12px',
                  background: 'rgba(0,0,0,0.06)',
                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <button
                  type="button"
                  className="zolaid-btn timeline-btn"
                  onClick={() => setChromeHidden(false)}
                  title="Show toolbar (permissions, timeline, export, views)"
                >
                  Show toolbar
                </button>
              </div>
            )}
            {/* Code mode compact header */}
            {isCodeMode && !codeChromeOpen && !chromeHidden && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '8px 12px',
                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                  background: '#fff',
                }}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
                  <div style={{ fontWeight: 900, whiteSpace: 'nowrap' }}>Code</div>
                  <div className={`verify-pill ${verification.ok ? 'ok' : 'bad'}`} style={{ marginLeft: 6 }}>
                    {verification.ok ? '\u2705 Verified' : `\u274C Errors: ${verification.errorCount}`}
                  </div>
                  <div style={{ opacity: 0.7, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {currentTask || latestAction?.description || 'Waiting for instructions'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {awaitingApproval && currentAction ? (
                    <button className="timeline-btn" onClick={approveNext} title="Approve next action" type="button">
                      Approve
                    </button>
                  ) : null}
                  <button
                    className="timeline-btn"
                    onClick={() => setCodeChromeOpen(true)}
                    title="Show permissions / timeline / export / deploy"
                    type="button"
                  >
                    Show toolbar
                  </button>
                </div>
              </div>
            )}
            {/* Approval + Permissions */}
            {!chromeHidden && ((!isCodeMode || codeChromeOpen)) && (
            <div className="zolaid-permissions-bar">
              <label className="zolaid-permission-toggle">
                <input
                  type="checkbox"
                  checked={approvalMode}
                  onChange={(e) => setApprovalMode(e.target.checked)}
                />
                Require approval
              </label>
              <div className="zolaid-permission-chips">
                {['click', 'type', 'createFile', 'updatePreview', 'moveCursor'].map(t => (
                  <button
                    key={t}
                    className={`perm-chip ${allowedTypes.includes(t) ? 'on' : 'off'}`}
                    onClick={() => {
                      setAllowedTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
                    }}
                    title="Toggle permission"
                    type="button"
                  >
                    {t}
                  </button>
                ))}
              </div>
              <button
                className="timeline-btn"
                type="button"
                onClick={() => {
                  if (isCodeMode) setCodeChromeOpen(false);
                  setChromeHidden(true);
                }}
                title="Hide toolbar (more space)"
                style={{ marginLeft: 10 }}
              >
                Hide
              </button>
            </div>
            )}

            {awaitingApproval && currentAction && (
              <div className="zolaid-approval-banner">
                <div className="approval-left">
                  <div className="approval-title">Approval required</div>
                  <div className="approval-desc">{currentAction.description || currentAction.type}</div>
                </div>
                <div className="approval-actions">
                  <button className="approve-btn" onClick={approveNext}>Approve</button>
                  <button className="skip-btn" onClick={skipNext}>Skip</button>
                  <button className="rewind-btn" onClick={() => resetTo(Math.max(0, currentActionIndex - 1))}>Rewind</button>
                </div>
              </div>
            )}

            {/* Status Bar */}
            {!chromeHidden && ((!isCodeMode || codeChromeOpen)) && (
            <div className="zolaid-status-bar">
              <div className="status-bar-left">
                <div className="zolaid-logo">
                  <img src="/logo.png" alt="Zolaid" style={{ width: '24px', height: '24px' }} />
                </div>
                <div className="status-text-secondary">
                  {hasActiveTask ? 'Zolaid is active' : 'Zolaid is inactive'}
                </div>
              </div>
              <div className="status-bar-divider"></div>
              <div className="status-bar-right">
                <div className="task-status">
                  {currentTask || latestAction?.description || 'Waiting for instructions'}
                </div>
              </div>
            </div>
            )}

            {/* Timeline */}
            {!chromeHidden && ((!isCodeMode || codeChromeOpen)) && (
            <div className="zolaid-timeline-bar">
              <div className="timeline-label">
                Timeline: {timelineIndex}/{Math.max(previewHistory.length - 1, 0)}
              </div>
              <input
                className="timeline-slider"
                type="range"
                min={0}
                max={Math.max(previewHistory.length - 1, 0)}
                value={Math.min(timelineIndex, Math.max(previewHistory.length - 1, 0))}
                onChange={(e) => setTimelineIndex(parseInt(e.target.value, 10))}
              />
              <div className={`verify-pill ${verification.ok ? 'ok' : 'bad'}`}>
                {verification.ok ? '\u2705 Verified (no runtime errors)' : `\u274C Errors: ${verification.errorCount}`}
              </div>
              <button
                className="timeline-btn"
                onClick={() => {
                  // Branch: rewind executor to timelineIndex and clear future runtime events
                  resetTo(timelineIndex);
                  setRuntimeEvents([]);
                }}
                title="Rewind execution to this point"
                type="button"
              >
                âª Rewind
              </button>

              {canRequestFix && (
                <button
                  className="timeline-btn"
                  onClick={() => {
                    const last = verification.lastError;
                    onRequestFix({
                      lastError: last,
                      errorCount: verification.errorCount,
                      preview: previewContent,
                      recentEvents: runtimeEvents.slice(-50),
                    });
                  }}
                  title="Ask Zolaid to propose a fix based on runtime errors"
                  type="button"
                >
                  {'\uD83D\uDEE0\uFE0F Fix errors'}
                </button>
              )}
            </div>
            )}

            {/* Export */}
            {!chromeHidden && ((!isCodeMode || codeChromeOpen)) && (
            <div className="zolaid-export-bar">
              <div className="export-label">Export</div>
              <div className="export-buttons">
                <button
                  className="export-btn export-btn-icon"
                  type="button"
                  onClick={() => copyText(buildStandaloneHtml(previewContent), 'Copied HTML')}
                  title="Copy a runnable HTML file (includes CSS & JS inline)"
                  aria-label="Copy HTML"
                >
                  <span className="export-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </span>
                  <span className="sr-only">Copy HTML</span>
                </button>
                <button
                  className="export-btn export-btn-icon"
                  type="button"
                  onClick={() => copyText(previewContent.css || '', 'Copied CSS')}
                  title="Copy CSS"
                  aria-label="Copy CSS"
                >
                  <span className="export-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10 13a5 5 0 0 1 0-7l1-1a5 5 0 0 1 7 7l-1 1"></path>
                      <path d="M14 11a5 5 0 0 1 0 7l-1 1a5 5 0 0 1-7-7l1-1"></path>
                    </svg>
                  </span>
                  <span className="sr-only">Copy CSS</span>
                </button>
                <button
                  className="export-btn export-btn-icon"
                  type="button"
                  onClick={() => copyText(previewContent.js || '', 'Copied JS')}
                  title="Copy JavaScript"
                  aria-label="Copy JavaScript"
                >
                  <span className="export-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 18a2 2 0 0 1-2 2h-1"></path>
                      <path d="M8 6v10a2 2 0 0 0 2 2h1"></path>
                      <path d="M3 12h3"></path>
                      <path d="M18 12h3"></path>
                    </svg>
                  </span>
                  <span className="sr-only">Copy JS</span>
                </button>
                <button
                  className="export-btn export-btn-icon"
                  type="button"
                  onClick={() =>
                    copyText(
                      JSON.stringify(
                        { html: previewContent.html || '', css: previewContent.css || '', js: previewContent.js || '' },
                        null,
                        2
                      ),
                      'Copied all'
                    )
                  }
                  title="Copy as JSON {html, css, js}"
                  aria-label="Copy all"
                >
                  <span className="export-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M8 6h13"></path>
                      <path d="M8 12h13"></path>
                      <path d="M8 18h13"></path>
                      <path d="M3 6h.01"></path>
                      <path d="M3 12h.01"></path>
                      <path d="M3 18h.01"></path>
                    </svg>
                  </span>
                  <span className="sr-only">Copy All</span>
                </button>
                <button
                  className="export-btn export-btn-icon"
                  type="button"
                  onClick={() => downloadTextFile('index.html', buildStandaloneHtml(previewContent), 'text/html;charset=utf-8')}
                  title="Download a runnable HTML file"
                  aria-label="Download HTML"
                >
                  <span className="export-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 3v12"></path>
                      <path d="M7 10l5 5 5-5"></path>
                      <path d="M5 21h14"></path>
                    </svg>
                  </span>
                  <span className="sr-only">Download HTML</span>
                </button>
                <button
                  className="export-btn export-btn-icon"
                  type="button"
                  onClick={async () => {
                    try {
                      setToast('Preparing ZIP\u2026');
                      let serverFiles = {};
                      if (!privacyEnabled) {
                        try {
                          serverFiles = await fetchIdeExportFromDevTools();
                        } catch (_) {
                          serverFiles = {};
                        }
                      }
                      const gen = buildIdeProjectZipEntries(
                        generatedFiles && typeof generatedFiles === 'object' ? generatedFiles : {}
                      );
                      const merged = { ...serverFiles, ...gen };
                      const keys = Object.keys(merged).filter(Boolean);
                      if (!keys.length) {
                        const htmlDoc = buildStandaloneHtml(previewContent);
                        const files = buildStaticZipFiles({
                          htmlDoc,
                          css: previewContent.css || '',
                          js: previewContent.js || '',
                          includeSplitFiles: true,
                        });
                        await createZipAndDownload('zolaid-export.zip', files);
                        setToast('Downloaded preview ZIP (no project files found)');
                        window.clearTimeout(downloadTextFile.__toastTimer);
                        downloadTextFile.__toastTimer = window.setTimeout(() => setToast(null), 2200);
                        return;
                      }
                      await createZipAndDownload('zolaid-ide-project.zip', merged);
                      setToast(`Downloaded ZIP (${keys.length} files: repo + Generated)`);
                      window.clearTimeout(downloadTextFile.__toastTimer);
                      downloadTextFile.__toastTimer = window.setTimeout(() => setToast(null), 2200);
                    } catch (e) {
                      setToast('ZIP download failed');
                      window.clearTimeout(downloadTextFile.__toastTimer);
                      downloadTextFile.__toastTimer = window.setTimeout(() => setToast(null), 2200);
                    }
                  }}
                  title="Download ZIP: monorepo files visible to DevTools (repo + .zolaid_generated) plus in-memory Generated; unsaved editor state wins on path clashes. Needs backend with DevTools enabled."
                  aria-label="Download ZIP"
                >
                  <span className="export-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 3h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"></path>
                      <path d="M10 3v6"></path>
                      <path d="M14 3v6"></path>
                      <path d="M10 9h4"></path>
                      <path d="M12 9v12"></path>
                    </svg>
                  </span>
                  <span className="sr-only">Download ZIP</span>
                </button>
                <button
                  className="export-btn export-btn-icon"
                  type="button"
                  onClick={() => setDeployOpen(true)}
                  title="Deploy current preview"
                  aria-label="Deploy"
                >
                  <span className="export-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2l3 7 7 3-7 3-3 7-3-7-7-3 7-3 3-7z"></path>
                    </svg>
                  </span>
                  <span className="sr-only">Deploy</span>
                </button>
              </div>
            </div>
            )}

            {deployOpen && (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0,0,0,0.5)',
                  zIndex: 10050,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 16,
                }}
                onClick={() => setDeployOpen(false)}
              >
                <div
                  style={{ width: 560, maxWidth: '96vw', background: '#fff', borderRadius: 14, padding: 14 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontWeight: 900 }}>
                      {'Deploy \u2192 '}{deployProvider === 'vercel' ? 'Vercel' : 'GitHub Pages'}
                    </div>
                    <button className="export-btn" type="button" onClick={() => setDeployOpen(false)}>
                      Close
                    </button>
                  </div>
                  <div style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
                    Requires backend env: <code>DEPLOY_ENABLED=true</code>. Token is used once and not stored.
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={{ fontSize: 12, fontWeight: 800 }}>Provider</label>
                    <select
                      value={deployProvider}
                      onChange={(e) => {
                        setDeployProvider(String(e.target.value || 'github-pages'));
                        setDeployStatus('');
                        setDeployUrl('');
                      }}
                      style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                    >
                      <option value="github-pages">GitHub Pages</option>
                      <option value="vercel">Vercel</option>
                      <option value="netlify">Netlify</option>
                      <option value="aws-s3">AWS S3 (static website)</option>
                      <option value="aws-cloudfront">AWS CloudFront (CDN + HTTPS)</option>
                      <option value="gcp-gcs">Google Cloud Storage</option>
                      <option value="cloudflare-pages">Cloudflare Pages</option>
                      <option value="firebase-hosting">Firebase Hosting</option>
                      <option value="azure-storage">Microsoft Azure (Storage Static Website)</option>
                    </select>

                    {(deployProvider === 'aws-s3' || deployProvider === 'aws-cloudfront') && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ marginTop: 2, fontSize: 12, opacity: 0.8 }}>
                          Needs IAM perms like <code>s3:CreateBucket</code>, <code>s3:PutObject</code>, <code>s3:PutBucketWebsite</code> (and for public: <code>s3:PutBucketPolicy</code>, <code>s3:PutPublicAccessBlock</code>).
                        </div>
                        {deployProvider === 'aws-cloudfront' && (
                          <div style={{ marginTop: 2, fontSize: 12, opacity: 0.8 }}>
                            Also needs <code>cloudfront:CreateDistribution</code>. Uses S3 <b>website endpoint</b> as origin (bucket must be public).
                          </div>
                        )}
                        <label style={{ fontSize: 12, fontWeight: 800 }}>Region</label>
                        <input
                          value={awsRegion}
                          onChange={(e) => setAwsRegion(e.target.value)}
                          placeholder="us-east-1"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                        <label style={{ fontSize: 12, fontWeight: 800 }}>Bucket</label>
                        <input
                          value={awsBucket}
                          onChange={(e) => setAwsBucket(e.target.value)}
                          placeholder="my-zolaid-site-bucket"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                        <label style={{ fontSize: 12, fontWeight: 800 }}>Access key ID</label>
                        <input
                          value={awsAccessKeyId}
                          onChange={(e) => setAwsAccessKeyId(e.target.value)}
                          placeholder="AKIA\u2026"
                          type="password"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                        <label style={{ fontSize: 12, fontWeight: 800 }}>Secret access key</label>
                        <input
                          value={awsSecretAccessKey}
                          onChange={(e) => setAwsSecretAccessKey(e.target.value)}
                          placeholder="\u2026"
                          type="password"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                        <label style={{ fontSize: 12, fontWeight: 800 }}>Session token (optional)</label>
                        <input
                          value={awsSessionToken}
                          onChange={(e) => setAwsSessionToken(e.target.value)}
                          placeholder="\u2026"
                          type="password"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                        {deployProvider === 'aws-s3' && (
                          <label style={{ fontSize: 12, fontWeight: 800, display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input type="checkbox" checked={awsMakePublic} onChange={(e) => setAwsMakePublic(e.target.checked)} />
                            Make public (set bucket policy)
                          </label>
                        )}
                      </div>
                    )}

                    {deployProvider === 'gcp-gcs' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ marginTop: 2, fontSize: 12, opacity: 0.8 }}>
                          Paste a <b>service account JSON</b> with Storage permissions (create bucket optional; upload required).
                        </div>
                        <label style={{ fontSize: 12, fontWeight: 800 }}>Bucket</label>
                        <input
                          value={gcpBucket}
                          onChange={(e) => setGcpBucket(e.target.value)}
                          placeholder="my-zolaid-site-bucket"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                        <label style={{ fontSize: 12, fontWeight: 800 }}>Service account JSON</label>
                        <textarea
                          value={gcpServiceAccountJson}
                          onChange={(e) => setGcpServiceAccountJson(e.target.value)}
                          placeholder='{"type":"service_account",...}'
                          rows={6}
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', fontSize: 11 }}
                        />
                      </div>
                    )}

                    {deployProvider === 'cloudflare-pages' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ marginTop: 2, fontSize: 12, opacity: 0.8 }}>
                          Needs a Cloudflare API token with Pages permissions. Project must already exist in your Cloudflare account.
                        </div>
                        <label style={{ fontSize: 12, fontWeight: 800 }}>Account ID</label>
                        <input
                          value={cfAccountId}
                          onChange={(e) => setCfAccountId(e.target.value)}
                          placeholder="\u2026"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                        <label style={{ fontSize: 12, fontWeight: 800 }}>Project name</label>
                        <input
                          value={cfProjectName}
                          onChange={(e) => setCfProjectName(e.target.value)}
                          placeholder="zolaid-site"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                        <label style={{ fontSize: 12, fontWeight: 800 }}>API token</label>
                        <input
                          value={cfToken}
                          onChange={(e) => setCfToken(e.target.value)}
                          placeholder="\u2026"
                          type="password"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                      </div>
                    )}

                    {deployProvider === 'firebase-hosting' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ marginTop: 2, fontSize: 12, opacity: 0.8 }}>
                          {'Needs a Firebase CLI token (for CI) + your project id. Site id is optional (defaults to the project\u2019s default hosting site).'}
                        </div>
                        <label style={{ fontSize: 12, fontWeight: 800 }}>Project ID</label>
                        <input
                          value={fbProjectId}
                          onChange={(e) => setFbProjectId(e.target.value)}
                          placeholder="my-firebase-project"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                        <label style={{ fontSize: 12, fontWeight: 800 }}>Site ID (optional)</label>
                        <input
                          value={fbSiteId}
                          onChange={(e) => setFbSiteId(e.target.value)}
                          placeholder="my-site"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                        <label style={{ fontSize: 12, fontWeight: 800 }}>Firebase token</label>
                        <input
                          value={fbToken}
                          onChange={(e) => setFbToken(e.target.value)}
                          placeholder="\u2026"
                          type="password"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                      </div>
                    )}

                    {deployProvider === 'azure-storage' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ marginTop: 2, fontSize: 12, opacity: 0.8 }}>
                          Uploads to Azure Blob container <code>$web</code>. You must enable <b>Static website</b> on the Storage Account.
                        </div>
                        <label style={{ fontSize: 12, fontWeight: 800 }}>Connection string (recommended)</label>
                        <input
                          value={azureConnectionString}
                          onChange={(e) => setAzureConnectionString(e.target.value)}
                          placeholder="DefaultEndpointsProtocol=...;AccountName=...;AccountKey=...;EndpointSuffix=core.windows.net"
                          type="password"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                        <div style={{ fontSize: 12, opacity: 0.7 }}>Or use account name + key:</div>
                        <label style={{ fontSize: 12, fontWeight: 800 }}>Account name</label>
                        <input
                          value={azureAccountName}
                          onChange={(e) => setAzureAccountName(e.target.value)}
                          placeholder="mystorageaccount"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                        <label style={{ fontSize: 12, fontWeight: 800 }}>Account key</label>
                        <input
                          value={azureAccountKey}
                          onChange={(e) => setAzureAccountKey(e.target.value)}
                          placeholder="\u2026"
                          type="password"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                      </div>
                    )}

                    {deployProvider !== 'aws-s3' &&
                      deployProvider !== 'aws-cloudfront' &&
                      deployProvider !== 'gcp-gcs' &&
                      deployProvider !== 'cloudflare-pages' &&
                      deployProvider !== 'firebase-hosting' &&
                      deployProvider !== 'azure-storage' && (
                      <>
                        <label style={{ fontSize: 12, fontWeight: 800 }}>
                          {deployProvider === 'vercel' ? 'Project name' : deployProvider === 'netlify' ? 'Site name' : 'Repo name'}
                        </label>
                        <input
                          value={deployRepoName}
                          onChange={(e) => setDeployRepoName(e.target.value)}
                          placeholder="zolaid-site"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                        <label style={{ fontSize: 12, fontWeight: 800 }}>
                          {deployProvider === 'vercel'
                            ? 'Vercel token'
                            : deployProvider === 'netlify'
                              ? 'Netlify token'
                              : 'GitHub token (classic PAT)'}
                        </label>
                        <input
                          value={deployToken}
                          onChange={(e) => setDeployToken(e.target.value)}
                          placeholder={deployProvider === 'vercel' ? 'vercel_\u2026' : deployProvider === 'netlify' ? 'nfp_\u2026' : 'ghp_\u2026'}
                          type="password"
                          style={{ padding: 10, borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)' }}
                        />
                      </>
                    )}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                      <button
                        className="export-btn"
                        type="button"
                        onClick={
                          deployProvider === 'vercel'
                            ? deployToVercel
                            : deployProvider === 'netlify'
                              ? deployToNetlify
                              : deployProvider === 'aws-s3'
                                ? deployToAwsS3
                                : deployProvider === 'aws-cloudfront'
                                  ? deployToAwsCloudFront
                                  : deployProvider === 'gcp-gcs'
                                    ? deployToGcpGcs
                                    : deployProvider === 'cloudflare-pages'
                                      ? deployToCloudflarePages
                                      : deployProvider === 'firebase-hosting'
                                        ? deployToFirebaseHosting
                                        : deployProvider === 'azure-storage'
                                          ? deployToAzureStorage
                              : deployToGitHubPages
                        }
                      >
                        Deploy now
                      </button>
                      <div style={{ fontSize: 12, opacity: 0.85 }}>{deployStatus}</div>
                    </div>
                    {deployUrl ? (
                      <div style={{ marginTop: 6, fontSize: 12 }}>
                        URL:{" "}
                        <a href={deployUrl} target="_blank" rel="noreferrer">
                          {deployUrl}
                        </a>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            {(!isActive && inactiveMinimal) ? (
              <div className="zolaid-inactive-minimal">
                <div className="zolaid-inactive-title">Zolaid is inactive</div>
                <div className="zolaid-inactive-icons">
                  <button
                    type="button"
                    className="zolaid-inactive-iconbtn"
                    title="Show workspace"
                    onClick={() => setInactiveMinimal(false)}
                  >
                    {'\uD83D\uDC41'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {artifacts && typeof artifacts === 'object' && (
                  <details className="zolaid-artifacts" open>
                    <summary>Reasoning artifacts</summary>
                    {artifacts.plan && <div className="artifact-block"><div className="artifact-title">Plan</div><pre>{artifacts.plan}</pre></div>}
                    {artifacts.simulation && <div className="artifact-block"><div className="artifact-title">Simulation</div><pre>{artifacts.simulation}</pre></div>}
                    {artifacts.decisions && <div className="artifact-block"><div className="artifact-title">Decisions</div><pre>{artifacts.decisions}</pre></div>}
                    {artifacts.outcomes && <div className="artifact-block"><div className="artifact-title">Outcomes</div><pre>{artifacts.outcomes}</pre></div>}
                  </details>
                )}

                {/* View Mode Toggle \u2014 Code (1), Preview (2), VS Code (3) when embed URL set */}
                {!chromeHidden && (
                <div className={`view-mode-toggle ${viewBarHidden ? 'collapsed' : ''}`} ref={viewDropdownRef}>
                  <button
                    type="button"
                    className="view-mode-collapse-btn"
                    onClick={() => { setViewBarHidden((v) => !v); setViewDropdownOpen(false); }}
                    title={viewBarHidden ? `Show view modes (1\u2013${hasVscodeEmbed ? '3' : '2'})` : 'Hide view modes'}
                    aria-label={viewBarHidden ? 'Show view modes' : 'Hide view modes'}
                  >
                    {viewBarHidden ? 'â–¸ Views' : 'â–¾ Hide'}
                  </button>
                  {viewBarHidden ? (
                    <div className="view-mode-current-wrap">
                      <button
                        type="button"
                        className="view-mode-btn active view-mode-current"
                        onClick={(e) => { e.stopPropagation(); setViewDropdownOpen((v) => !v); }}
                        title={`Current view. Click to switch (1\u2013${hasVscodeEmbed ? '3' : '2'})`}
                        aria-label={`Current: ${viewMode === 'preview' ? 'Preview' : viewMode === 'vscode' ? 'VS Code' : 'Code'}. Click to switch view.`}
                        aria-expanded={viewDropdownOpen}
                        aria-haspopup="listbox"
                      >
                        {viewMode === 'preview' ? '\uD83C\uDF10 Preview' : viewMode === 'vscode' ? '\uD83E\uDDE9 VS Code' : 'Zolaid editor'}
                        <span className="view-mode-chevron">{viewDropdownOpen ? ' â–´' : ' â–¾'}</span>
                      </button>
                      {viewDropdownOpen && (
                        <div className="view-mode-dropdown" role="listbox" aria-label="Switch sandbox view">
                          {[
                            {
                              id: 'code',
                              label: 'Zolaid editor',
                              title: 'In-app editor where AI applies file changes and preview syncs (1). Not full VS Code.',
                            },
                            { id: 'preview', label: '\uD83C\uDF10 Preview', title: 'Live preview (2)' },
                            ...(hasVscodeEmbed
                              ? [{ id: 'vscode', label: '\uD83E\uDDE9 VS Code', title: 'Full VS Code in browser via code-server (3)' }]
                              : []),
                          ].map(({ id, label, title }) => (
                            <button
                              key={id}
                              type="button"
                              role="option"
                              aria-selected={viewMode === id}
                              className={`view-mode-dropdown-item ${viewMode === id ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!validViewModes.includes(id)) return;
                                setViewMode(id);
                                setViewDropdownOpen(false);
                              }}
                              title={title}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className={`view-mode-btn ${viewMode === 'code' ? 'active' : ''}`}
                    onClick={() => {
                      setViewMode('code');
                    }}
                    title={"Zolaid editor \u2014 AI writes project files here; preview syncs (press 1). For full VS Code use the \uD83E\uDDE9 VS Code tab (code-server)."}
                    aria-label="Zolaid editor (press 1)"
                  >
                    Zolaid
                  </button>
                  <button
                    type="button"
                    className={`view-mode-btn ${viewMode === 'preview' ? 'active' : ''}`}
                    onClick={() => {
                      setViewMode('preview');
                    }}
                    title={"Live preview \u2014 full workspace (press 2)"}
                    aria-label={"Preview \u2014 full page (press 2)"}
                  >
                    {'\uD83C\uDF10 Preview'}
                  </button>
                  {hasVscodeEmbed ? (
                    <button
                      type="button"
                      className={`view-mode-btn ${viewMode === 'vscode' ? 'active' : ''}`}
                      onClick={() => {
                        setViewMode('vscode');
                      }}
                      title={"Full VS Code (code-server) in browser \u2014 press 3. Requires a running server; vscode.dev cannot be embedded."}
                      aria-label="VS Code code-server (press 3)"
                    >
                      {'\uD83E\uDDE9 VS Code'}
                    </button>
                  ) : null}
                </div>
                )}
                {!chromeHidden && !hasVscodeEmbed && (
                  <p className="zolaid-vscode-unavailable-hint" role="note">
                    <strong>VS Code tab:</strong> not shown in this build. Set{' '}
                    <code className="zolaid-vscode-hint-code">REACT_APP_VSCODE_EMBED_URL</code> for production, or run a{' '}
                    <strong>development</strong> build (defaults to{' '}
                    <code className="zolaid-vscode-hint-code">http://127.0.0.1:8080</code>). Use self-hosted{' '}
                    <a href="https://github.com/coder/code-server" target="_blank" rel="noopener noreferrer">
                      code-server
                    </a>
                    . <strong>Code</strong> here is{' '}
                    <a href="https://microsoft.github.io/monaco-editor/" target="_blank" rel="noopener noreferrer">
                      Monaco
                    </a>{' '}
                    {'with a VS Code\u2013like layout \u2014 not the full VS Code product.'}
                  </p>
                )}

                {/* Main Workspace \u2014 Code, Preview, optional VS Code: one full page at a time; panes stay mounted */}
                <div className="zolaid-workspace-area">
                  <div className="workspace-active zolaid-ide-preview-workspace">
                    <div
                      className="zolaid-workspace-page zolaid-workspace-page-code"
                      style={{
                        flex: 1,
                        minHeight: 0,
                        minWidth: 0,
                        overflow: 'hidden',
                        display: isCodeMode ? 'flex' : 'none',
                        flexDirection: 'column',
                      }}
                    >
                      <div
                        className="zolaid-ide-panel"
                        style={{
                          flex: 1,
                          minWidth: 0,
                          minHeight: 0,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <DeveloperIDE
                          generatedFiles={generatedFiles}
                          onUpdateGeneratedFile={updateGeneratedFile}
                          onSyncWebPreview={syncWebPreviewFromPath}
                          onDeleteGeneratedFile={deleteGeneratedFile}
                          onRenameGeneratedFile={renameGeneratedFile}
                          onQueueActions={enqueueActions}
                          onIdeActiveGeneratedPathChange={handleIdeGeneratedPathForPreview}
                        />
                      </div>
                    </div>
                    <div
                      className="zolaid-workspace-page zolaid-workspace-page-preview"
                      style={{
                        flex: 1,
                        minHeight: 0,
                        minWidth: 0,
                        overflow: 'hidden',
                        display: isPreviewMode ? 'flex' : 'none',
                        flexDirection: 'column',
                      }}
                    >
                      <div
                        className="zolaid-preview-stack"
                        style={{
                          flex: 1,
                          minWidth: 0,
                          minHeight: 0,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            minHeight: 0,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <LivePreview
                            fullDoc={previewFullDoc || ""}
                            html={previewFullDoc ? "" : previewContent.html}
                            css={previewContent.css}
                            js={previewContent.js}
                            isVisible={true}
                            hideChrome={embedded}
                            onRuntimeEvent={handleRuntimeEvent}
                            enablePreviewEdit={true}
                            hostMessage={previewHostNav}
                            errorOverlay={
                              verification.ok
                                ? null
                                : {
                                    title: 'Preview runtime errors',
                                    message:
                                      verification.lastError?.type === 'error'
                                        ? (verification.lastError.message || '')
                                        : verification.lastError?.type === 'unhandledrejection'
                                          ? (verification.lastError.reason || '')
                                          : verification.lastError?.type === 'console'
                                            ? (Array.isArray(verification.lastError.args)
                                                ? verification.lastError.args.join(' ')
                                                : '')
                                            : '',
                                  }
                            }
                          />
                        </div>
                      </div>
                    </div>
                    {hasVscodeEmbed ? (
                    <div
                      className="zolaid-workspace-page zolaid-workspace-page-vscode"
                      style={{
                        flex: 1,
                        minHeight: 0,
                        minWidth: 0,
                        overflow: 'hidden',
                        display: isVSCodeMode ? 'flex' : 'none',
                        flexDirection: 'column',
                        background: 'var(--z-panel-bg, #1e1e1e)',
                      }}
                    >
                      <div className="zolaid-vscode-embed-bar">
                        <div className="zolaid-vscode-embed-bar-main">
                          <span className="zolaid-vscode-embed-label">Full VS Code (code-server)</span>
                          <span className="zolaid-vscode-embed-url" title={vsCodeEmbedUrl}>
                            {vsCodeEmbedUrl.length > 56 ? `${vsCodeEmbedUrl.slice(0, 53)}\u2026` : vsCodeEmbedUrl}
                          </span>
                        </div>
                        <a
                          className="zolaid-vscode-embed-open"
                          href={vsCodeEmbedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={"Opens the same URL in a new tab (use if the frame is blank \u2014 github.dev / vscode.dev block embedding)"}
                        >
                          {'Open in new tab \u2197'}
                        </a>
                      </div>
                      {vscodeEmbedMixedContent ? (
                        <div className="zolaid-vscode-embed-warn" role="alert">
                          This app is loaded over <strong>HTTPS</strong>, but the editor URL is <strong>HTTP</strong>. Your
                          browser will not show it inside the frame (mixed content). Use <strong>Open in new tab</strong>,
                          or host code-server on <strong>HTTPS</strong> and set{' '}
                          <code className="zolaid-vscode-hint-code">REACT_APP_VSCODE_EMBED_URL</code> to that URL.
                        </div>
                      ) : (
                        <div className="zolaid-vscode-embed-hint" role="note">
                          <strong>Website starter kit:</strong>{' it lives in the '}<strong>Code</strong>{' tab only \u2014 open '}
                          <strong>Explorer</strong>{' \u2192 expand '}<strong>Generated</strong>{' \u2192 '}<code className="zolaid-vscode-hint-code">frontend/index.html</code>{' '}
                          (and the rest of <code className="zolaid-vscode-hint-code">frontend/*</code>). This VS Code iframe
                          is your separate code-server folder; it does not receive Zolaid&apos;s in-memory starter unless you
                          copy files yourself.
                          <br />
                          <br />
                          This tab embeds{' '}
                          <a href="https://github.com/coder/code-server" target="_blank" rel="noopener noreferrer">
                            code-server
                          </a>{' '}
                          only (not the built-in Monaco workspace). Blank frame? Another app may use the port, the server
                          {'isn\'t running, or framing is blocked \u2014 try '}<strong>{'Open in new tab \u2197'}</strong>{'. Example: '}
                          <code className="zolaid-vscode-hint-code">npx code-server@latest --bind-addr 127.0.0.1:9090</code>
                        </div>
                      )}
                      <iframe
                        title="VS Code (code-server)"
                        src={vscodeEmbedMixedContent ? 'about:blank' : vsCodeEmbedUrl}
                        className="zolaid-vscode-embed-frame"
                        style={{ flex: 1, width: '100%', minHeight: 0, border: 'none' }}
                        allow="clipboard-read; clipboard-write"
                      />
                    </div>
                    ) : null}
                  </div>
                </div>

                {/* Task Queue / Progress Bar */}
                {hasActiveTask && (
                  <div className="zolaid-task-bar">
                    <div className="task-bar-content">
                      <div className="task-indicator">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 14 14" fill="none">
                          <circle
                            cx="6.99658"
                            cy="6.99902"
                            r="6.34375"
                            stroke="#f59e0b"
                            strokeWidth="1.3125"
                            strokeDasharray="2.13 1.42"
                            className={isExecuting ? 'spinning' : ''}
                          ></circle>
                        </svg>
                        <div className="task-info">
                          <div className="task-title">
                            {currentTask || currentAction?.description || 'Processing...'}
                          </div>
                          <div className="task-subtitle">
                            {isExecuting ? `Step ${executedActions.length + 1} of ${structuredActions.length}` : 'Waiting for user...'}
                          </div>
                        </div>
                      </div>
                      <div className="task-progress">
                        <span className="progress-text">
                          {Math.round(progress)}%
                        </span>
                        <button className="task-expand-btn" type="button">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m18 15-6-6-6 6"></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
